import { ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Calendar, CalendarClock, LogOut, Menu, Users, X, Download, RefreshCw, Share, SquarePlus } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { useOrientation } from '@/hooks/useOrientation';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useMyFamilies } from '@/hooks/useMyFamilies';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useRefreshContext } from '@/contexts/RefreshContext';

interface LayoutProps {
  children: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useTranslation('common');
  const { logout } = useAuth();
  const { isInstallable, isIosDevice, showInstallModal, showInstallPrompt, dismissInstallModal, triggerInstall } =
    usePwaInstall();
  const { isLandscape } = useOrientation();
  const scrollDirection = useScrollDirection();
  const { families } = useMyFamilies();
  const { refresh, isRefreshing } = useRefreshContext();

  useBodyScrollLock(sidebarOpen);

  const handleRefresh = () => {
    refresh(location.pathname);
  };

  const handleLogout = () => {
    logout();
  };

  const isActive = (path: string) => location.pathname === path;

  const allNavItems = [
    { path: '/my-families', labelKey: 'navigation.myFamilies', icon: Users, hasRefresh: true },
    { path: '/events', labelKey: 'navigation.calendar', icon: Calendar, hasRefresh: true },
    { path: '/upcoming', labelKey: 'navigation.upcoming', icon: CalendarClock, hasRefresh: true },
    { path: '/my-events', labelKey: 'navigation.past', icon: Calendar, hasRefresh: true },
  ];

  // Only show "My Families" tab when no families are registered
  const hasFamilies = families.length > 0;
  const navItems = hasFamilies ? allNavItems : allNavItems.filter((item) => item.path === '/my-families');

  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:bg-[var(--color-brand-white)] lg:border-r">
        <div className="flex items-center justify-center h-20 border-b">
          <img
            src="/logo.png"
            className="h-14 w-auto cursor-pointer "
            alt="GMHSC Logo"
            onClick={() => navigate('/my-families')}
          />
        </div>
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isCurrentlyActive = isActive(item.path);
            return (
              <div key={item.path} className="relative flex items-center gap-1">
                <Button
                  variant={isCurrentlyActive ? 'default' : 'ghost'}
                  className={`flex-1 justify-start py-2.5 px-3 ${
                    isCurrentlyActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-[var(--color-neutral-700)] hover:text-[var(--color-neutral-900)] hover:bg-[var(--color-neutral-300)]/20'
                  }`}
                  onClick={() => navigate(item.path)}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {t(item.labelKey)}
                </Button>
                {isCurrentlyActive && item.hasRefresh && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="h-9 w-9 shrink-0"
                    title={t('actions.refresh')}
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </Button>
                )}
              </div>
            );
          })}
        </nav>
        <div className="p-4 border-t bg-[var(--color-brand-white)] space-y-3">
          <LanguageSwitcher variant="full" />
          {isInstallable && (
            <Button
              variant="outline"
              className="w-full justify-start py-2.5 px-3 text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={showInstallPrompt}
            >
              <Download className="h-4 w-4 mr-3" />
              {t('navigation.installApp')}
            </Button>
          )}
          <Button
            variant="outline"
            className="w-full justify-start py-2.5 px-3 text-[var(--color-neutral-700)] hover:text-[var(--color-error-700)] hover:bg-[var(--color-error-100)]"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-3" />
            {t('navigation.logout')}
          </Button>
        </div>
      </aside>

      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-[var(--color-neutral-950)] bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[var(--color-brand-white)] z-50 lg:hidden flex flex-col">
            <div className="flex items-center justify-between h-20 border-b px-4">
              <img
                src="/logo.png"
                className="h-14 w-auto cursor-pointer "
                alt="GMHSC Logo"
                onClick={() => navigate('/my-families')}
              />
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <nav className="flex-1 p-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isCurrentlyActive = isActive(item.path);
                return (
                  <div key={item.path} className="relative flex items-center gap-1">
                    <Button
                      variant={isCurrentlyActive ? 'default' : 'ghost'}
                      className={`flex-1 justify-start ${
                        isCurrentlyActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-[var(--color-neutral-700)] hover:text-[var(--color-neutral-900)] hover:bg-[var(--color-neutral-300)]/20'
                      }`}
                      onClick={() => {
                        navigate(item.path);
                        setSidebarOpen(false);
                      }}
                    >
                      <Icon className="h-5 w-5 mr-3" />
                      {t(item.labelKey)}
                    </Button>
                    {isCurrentlyActive && item.hasRefresh && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="h-9 w-9 shrink-0"
                        title={t('actions.refresh')}
                      >
                        <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                      </Button>
                    )}
                  </div>
                );
              })}
            </nav>
            <div className="p-4 border-t space-y-3">
              <LanguageSwitcher variant="full" />
              <Button
                variant="outline"
                className="w-full justify-start text-[var(--color-neutral-700)] hover:text-[var(--color-error-700)] hover:bg-[var(--color-error-100)]"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-3" />
                {t('navigation.logout')}
              </Button>
            </div>
          </aside>
        </>
      )}

      <div className="flex-1 flex flex-col lg:ml-64">
        <header className="bg-[var(--color-brand-white)] border-b lg:hidden">
          <div className="px-4 sm:px-6">
            <div className="flex justify-between items-center h-16 landscape:h-12">
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} className="md:flex hidden">
                <Menu className="h-5 w-5" />
              </Button>
              <div className="flex-1 flex justify-center md:justify-start md:ml-4 items-center gap-4">
                {isInstallable && (
                  <Button variant="ghost" size="icon" className="md:hidden text-blue-600" onClick={showInstallPrompt}>
                    <Download className="h-5 w-5" />
                  </Button>
                )}
                <img
                  src="/logo.png"
                  className="h-14 w-auto cursor-pointer "
                  alt="GMHSC Logo"
                  onClick={() => navigate('/my-families')}
                />
              </div>
              <div className="flex items-center gap-2">
                <LanguageSwitcher variant="compact" />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  className="text-[var(--color-neutral-700)] hover:text-[var(--color-error-700)] md:hidden"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 pb-24 md:pb-0 landscape:pb-0">
          <div className="max-w-none mx-auto py-6 px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">{children}</div>
        </main>

        <nav
          className={`fixed bottom-0 left-0 right-0 bg-[var(--color-brand-white)] border-t md:hidden transition-transform duration-300 pb-safe ${isLandscape && scrollDirection === 'down' ? 'translate-y-full' : 'translate-y-0'}`}
        >
          <div className="flex justify-around items-center h-18 min-h-[72px]">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isCurrentlyActive = isActive(item.path);
              return (
                <div key={item.path} className="flex flex-col items-center justify-center flex-1 h-full relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(item.path)}
                    className={`flex flex-col items-center justify-center h-full flex-1 rounded-none w-full ${
                      isCurrentlyActive ? 'text-primary border-t-2 border-primary' : 'text-[var(--color-neutral-400)]'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs mt-1">{t(item.labelKey)}</span>
                  </Button>
                  {isCurrentlyActive && item.hasRefresh && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      className="absolute top-1 right-1 h-6 w-6 p-0"
                      title={t('actions.refresh')}
                    >
                      <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* PWA Install Modal */}
        {showInstallModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 space-y-4 animate-in slide-in-from-bottom duration-300">
              {isIosDevice ? (
                <>
                  {/* iOS Install Instructions */}
                  <h3 className="text-lg font-semibold text-gray-900">{t('navigation.iosInstall.title')}</h3>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                        1
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-700">{t('navigation.iosInstall.step1')}</p>
                        <div className="mt-1 inline-flex items-center gap-1 text-blue-600">
                          <Share className="h-5 w-5" />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                        2
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-700">{t('navigation.iosInstall.step2')}</p>
                        <div className="mt-1 inline-flex items-center gap-1 text-blue-600">
                          <SquarePlus className="h-5 w-5" />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                        3
                      </div>
                      <p className="text-gray-700">{t('navigation.iosInstall.step3')}</p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">{t('navigation.iosInstall.note')}</p>

                  <Button onClick={dismissInstallModal} className="w-full">
                    {t('navigation.iosInstall.dismiss')}
                  </Button>
                </>
              ) : (
                <>
                  {/* Android/Chrome Install Modal */}
                  <h3 className="text-lg font-semibold text-gray-900">{t('navigation.androidInstall.title')}</h3>

                  <p className="text-sm text-gray-500 bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    {t('navigation.androidInstall.browserNote')}
                  </p>

                  <div className="flex flex-col gap-3">
                    <Button onClick={triggerInstall} className="w-full bg-blue-600 hover:bg-blue-700">
                      <Download className="h-4 w-4 mr-2" />
                      {t('navigation.androidInstall.install')}
                    </Button>
                    <Button variant="outline" onClick={dismissInstallModal} className="w-full">
                      {t('navigation.androidInstall.dismiss')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
